import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, Copy, Link2, Loader2, Share2, X } from 'lucide-react'
import { writeClipboardText } from '../lib/clipboard'

type Proposal = { id: string; body: string; status: string; created: number }
type Share = { id: string; name: string | null; expires: number | null; revoked: number | null; proposals: Proposal[] }
type Invitation = { kind: 'none' } | { kind: 'copied' | 'copy-needed'; shareId: string; url: string }
type Policy = { kind: 'loading' } | { kind: 'editable' } | { kind: 'locked'; instructions: string }
type Props = { chatId: string; topicId: string | null; title: string }

const BASE = '/api/guest-mail/v1'
const BUTTON = 'min-h-11 rounded-xl border px-3 py-2 text-left text-sm transition-colors disabled:cursor-wait disabled:opacity-50'

function record(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function parseProposal(value: unknown): Proposal {
  if (!record(value) || typeof value.id !== 'string' || typeof value.body !== 'string'
    || typeof value.status !== 'string' || typeof value.created !== 'number') {
    throw new Error('The server returned an invalid guest request. Please refresh.')
  }
  return { id: value.id, body: value.body, status: value.status, created: value.created }
}

function parseShare(value: unknown): Share {
  if (!record(value) || typeof value.id !== 'string' || !Array.isArray(value.proposals)
    || !(value.name === null || typeof value.name === 'string')
    || !(value.expires === null || typeof value.expires === 'number')
    || !(value.revoked === null || typeof value.revoked === 'number')) {
    throw new Error('The server returned an invalid sharing link. Please refresh.')
  }
  return { id: value.id, name: value.name, expires: value.expires, revoked: value.revoked, proposals: value.proposals.map(parseProposal) }
}

class RequestError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.status = status
  }
}

async function request(path: string, data?: object): Promise<unknown> {
  const response = await fetch(`${BASE}${path}`, {
    method: data === undefined ? 'GET' : 'POST',
    credentials: 'include',
    cache: 'no-store',
    signal: AbortSignal.timeout(20_000),
    headers: data === undefined ? undefined : { 'Content-Type': 'application/json', 'X-Guest-Request': '1' },
    body: data === undefined ? undefined : JSON.stringify(data),
  })
  const value: unknown = await response.json()
  if (!response.ok) {
    throw new RequestError(record(value) && typeof value.detail === 'string' ? value.detail : 'Sharing could not be completed. Please retry.', response.status)
  }
  return value
}

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : 'Sharing could not be completed. Please retry.'
}

export default function GuestShareButton({ chatId, topicId, title }: Props) {
  const [open, setOpen] = useState(false)
  const [available, setAvailable] = useState(true)
  const [shares, setShares] = useState<Share[]>([])
  const [policy, setPolicy] = useState<Policy>({ kind: 'loading' })
  const [instructions, setInstructions] = useState('')
  const [defaultInstructions, setDefaultInstructions] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [customOpen, setCustomOpen] = useState(false)
  const [customDate, setCustomDate] = useState('')
  const [invitation, setInvitation] = useState<Invitation>({ kind: 'none' })
  const mounted = useRef(true)
  const loading = useRef(false)
  const copyGeneration = useRef(0)
  const trigger = useRef<HTMLButtonElement | null>(null)
  const sheet = useRef<HTMLDivElement | null>(null)
  const params = new URLSearchParams({ chat_id: chatId })
  if (topicId) params.set('topic_id', topicId)
  const scopePath = `/chat-shares?${params}`

  const refresh = useCallback(async () => {
    if (loading.current) return
    loading.current = true
    try {
      const value = await request(scopePath)
      if (!record(value) || !Array.isArray(value.shares) || typeof value.default_instructions !== 'string' || !value.default_instructions || !(value.instructions === null || typeof value.instructions === 'string')) throw new Error('Could not read this conversation’s sharing links.')
      const next = value.shares.map(parseShare)
      if (mounted.current) {
        setShares(next)
        setDefaultInstructions(value.default_instructions)
        setPolicy(typeof value.instructions === 'string' ? { kind: 'locked', instructions: value.instructions } : { kind: 'editable' })
        setAvailable(true)
      }
    } catch (e) {
      if (mounted.current) {
        if (e instanceof RequestError && e.status === 404) setAvailable(false)
        else if (open) setError(errorText(e))
      }
    } finally {
      loading.current = false
    }
  }, [open, scopePath])

  useEffect(() => {
    mounted.current = true
    void refresh()
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refresh()
    }, 5000)
    return () => { mounted.current = false; window.clearInterval(timer) }
  }, [refresh])

  const close = useCallback(() => {
    if (busy) return
    copyGeneration.current += 1
    setOpen(false)
    setInvitation({ kind: 'none' })
    setCustomOpen(false)
    setError(null)
    trigger.current?.focus()
  }, [busy])

  useEffect(() => {
    if (!open) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const focus = window.setTimeout(() => sheet.current?.querySelector<HTMLElement>('[data-share-instructions], [data-default-duration]')?.focus(), 0)
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); close() }
      if (event.key !== 'Tab' || !sheet.current) return
      const items = Array.from(sheet.current.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), textarea:not(:disabled), summary, [tabindex="0"]')).filter((item) => !item.hidden)
      const first = items[0], last = items[items.length - 1]
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
    }
    document.addEventListener('keydown', keydown)
    return () => {
      window.clearTimeout(focus)
      document.removeEventListener('keydown', keydown)
      document.body.style.overflow = previousOverflow
    }
  }, [close, open])

  async function copy(shareId: string, url: string) {
    const generation = ++copyGeneration.current
    const copied = await writeClipboardText(url)
    if (!mounted.current || generation !== copyGeneration.current) return
    setInvitation({ kind: copied ? 'copied' : 'copy-needed', shareId, url })
  }

  async function create(expires: number | null, action: string) {
    if (busy || policy.kind === 'loading') return
    setBusy(action)
    setError(null)
    try {
      const value = await request('/chat-shares', { chat_id: chatId, topic_id: topicId, expires, instructions: policy.kind === 'locked' ? null : instructions.trim() })
      if (!record(value) || typeof value.id !== 'string' || !/^[a-f0-9]{32}$/.test(value.id)
        || typeof value.url !== 'string' || new URL(value.url).protocol !== 'https:') {
        throw new Error('The server did not return a valid sharing link.')
      }
      if (!mounted.current) return
      setInvitation({ kind: 'copy-needed', shareId: value.id, url: value.url })
      await copy(value.id, value.url)
      await refresh()
    } catch (e) {
      if (mounted.current) { setError(`${errorText(e)} Check the links below before creating another.`); await refresh() }
    } finally {
      if (mounted.current) setBusy(null)
    }
  }

  async function act(shareId: string, proposalId: string | null, action: 'revoke' | 'approve' | 'dismiss' | 'reconcile') {
    if (busy) return
    setBusy(proposalId ?? shareId)
    setError(null)
    try {
      const path = proposalId ? `/shares/${shareId}/proposals/${proposalId}/${action}` : `/shares/${shareId}/${action}`
      const result = await request(path, {})
      if (action === 'revoke' && invitation.kind !== 'none' && invitation.shareId === shareId) {
        copyGeneration.current += 1
        setInvitation({ kind: 'none' })
      }
      if (action === 'reconcile' && record(result) && result.status !== 'sent') {
        throw new Error('Delivery is not confirmed. Nothing was resent.')
      }
      await refresh()
    } catch (e) {
      if (mounted.current) { setError(errorText(e)); await refresh() }
    } finally {
      if (mounted.current) setBusy(null)
    }
  }

  const activeShares = shares.filter((share) => share.revoked === null && (share.expires === null || share.expires > Date.now() / 1000))
  const pending = activeShares.reduce((count, share) => count + share.proposals.filter((proposal) => proposal.status === 'pending').length, 0)
  const minimumDate = new Date(Date.now() - new Date().getTimezoneOffset() * 60_000).toISOString().slice(0, 16)
  if (!available) return null

  return <>
    <button
      ref={trigger}
      type="button"
      aria-label={pending ? `Share this conversation, ${pending} guest requests` : 'Share this conversation'}
      aria-haspopup="dialog"
      aria-expanded={open}
      onClick={() => { setOpen(true); setError(null) }}
      className="relative inline-flex h-9 shrink-0 items-center gap-1 rounded-lg px-2 text-xs font-semibold text-stone-700 transition-colors hover:bg-stone-200 dark:text-stone-200 dark:hover:bg-stone-800"
      data-testid="chat-share-button"
    >
      <Share2 className="size-4" /><span>Share</span>
      {pending > 0 && <span className="grid min-w-4 place-items-center rounded-full bg-blue-600 px-1 text-[10px] text-white">{pending}</span>}
    </button>
    {open && createPortal(
      <div className="fixed inset-0 z-[90] flex items-end justify-center bg-stone-950/50 p-3 sm:items-center" onMouseDown={(e) => { if (e.target === e.currentTarget) close() }}>
        <div ref={sheet} role="dialog" aria-modal="true" aria-labelledby="guest-share-heading" aria-busy={busy !== null} className="max-h-[85dvh] w-full max-w-lg overflow-y-auto rounded-2xl border border-stone-200 bg-white p-4 shadow-2xl dark:border-stone-700 dark:bg-stone-900">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1"><h2 id="guest-share-heading" className="font-semibold text-stone-900 dark:text-stone-100">Share this conversation</h2><p className="mt-1 truncate text-xs text-stone-500 dark:text-stone-400">{title}</p></div>
            <button type="button" onClick={close} disabled={busy !== null} aria-label="Close sharing" className="grid size-9 place-items-center rounded-lg hover:bg-stone-100 disabled:opacity-50 dark:hover:bg-stone-800"><X className="size-4" /></button>
          </div>
          {defaultInstructions && <details className="mt-4 rounded-xl border border-stone-200 p-3 text-sm dark:border-stone-700" data-testid="sharing-safety-rules">
            <summary className="cursor-pointer font-semibold text-stone-900 dark:text-stone-100">Standard safety rules included automatically</summary>
            <p className="mt-2 whitespace-pre-wrap break-words text-xs leading-5 text-stone-600 dark:text-stone-300">{defaultInstructions}</p>
          </details>}
          <div className="mt-4">
            <label htmlFor="guest-share-instructions" className="text-sm font-semibold text-stone-900 dark:text-stone-100">{policy.kind === 'locked' ? 'Locked conversation instructions' : 'Extra instructions for this conversation (optional)'}</label>
            <textarea id="guest-share-instructions" data-share-instructions rows={4} maxLength={2000}
              value={policy.kind === 'locked' ? policy.instructions : instructions}
              readOnly={policy.kind === 'locked'} disabled={busy !== null || policy.kind === 'loading'}
              onChange={(event) => setInstructions(event.target.value)}
              aria-describedby="guest-share-instructions-help"
              placeholder="Example: Discuss this project only. Do not reveal private information or secrets."
              className="mt-2 block w-full resize-y rounded-xl border border-stone-300 bg-transparent p-3 text-sm text-stone-900 read-only:bg-stone-50 dark:border-stone-600 dark:text-stone-100 dark:read-only:bg-stone-800" />
            <p id="guest-share-instructions-help" className="mt-2 text-xs leading-5 text-stone-500 dark:text-stone-400">{policy.kind === 'locked' ? 'Fixed for every link to this conversation. Start a new conversation to change them.' : 'Add the purpose or audience here. These extra instructions lock when you create the link and apply to later requests. Guests can see them. You cannot edit them afterwards.'}</p>
            <p className="mt-2 text-xs leading-5 text-stone-500 dark:text-stone-400">Instructions guide the assistant; they cannot guarantee secrecy. Existing messages and future replies are shared. Every guest reply still needs your approval.</p>
          </div>
          <p className="mt-4 text-sm text-stone-600 dark:text-stone-300">How long should the link work? Tap a duration to lock the instructions and create and copy the link.</p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <button type="button" data-default-duration disabled={busy !== null || policy.kind === 'loading'} onClick={() => void create(Date.now() / 1000 + 7 * 86400, 'week')} className={`${BUTTON} col-span-2 border-blue-500 bg-blue-50 font-semibold text-blue-700 hover:bg-blue-100 dark:bg-blue-950/40 dark:text-blue-200`}><span className="flex items-center justify-between"><span>1 week</span><span className="text-xs font-normal">Default</span></span></button>
            <button type="button" disabled={busy !== null || policy.kind === 'loading'} onClick={() => void create(Date.now() / 1000 + 86400, 'day')} className={`${BUTTON} border-stone-200 hover:bg-stone-50 dark:border-stone-700 dark:hover:bg-stone-800`}>1 day</button>
            <button type="button" disabled={busy !== null || policy.kind === 'loading'} onClick={() => { const month = new Date(); month.setMonth(month.getMonth() + 1); void create(month.getTime() / 1000, 'month') }} className={`${BUTTON} border-stone-200 hover:bg-stone-50 dark:border-stone-700 dark:hover:bg-stone-800`}>1 month</button>
            <button type="button" disabled={busy !== null || policy.kind === 'loading'} onClick={() => void create(null, 'never')} className={`${BUTTON} border-stone-200 hover:bg-stone-50 dark:border-stone-700 dark:hover:bg-stone-800`}>Until revoked</button>
            <button type="button" disabled={busy !== null || policy.kind === 'loading'} onClick={() => setCustomOpen((value) => !value)} aria-expanded={customOpen} className={`${BUTTON} border-stone-200 hover:bg-stone-50 dark:border-stone-700 dark:hover:bg-stone-800`}>Choose date…</button>
          </div>
          {customOpen && <form className="mt-3 flex flex-wrap items-end gap-2" onSubmit={(event) => { event.preventDefault(); const seconds = new Date(customDate).getTime() / 1000; if (!Number.isFinite(seconds) || seconds <= Date.now() / 1000) { setError('Choose a future date and time.'); return } void create(seconds, 'custom') }}>
            <label className="min-w-0 flex-1 text-xs font-medium text-stone-600 dark:text-stone-300">Link expires<input type="datetime-local" min={minimumDate} required value={customDate} onChange={(e) => setCustomDate(e.target.value)} disabled={busy !== null || policy.kind === 'loading'} className="mt-1 block min-h-11 w-full rounded-lg border border-stone-300 bg-transparent p-2 text-sm dark:border-stone-600" /></label>
            <button type="submit" disabled={busy !== null || policy.kind === 'loading'} className="min-h-11 rounded-lg bg-blue-600 px-3 text-sm font-medium text-white disabled:opacity-50">Create and copy</button>
          </form>}
          {busy && <p role="status" className="mt-3 flex items-center gap-2 text-sm text-stone-500"><Loader2 className="size-4 animate-spin" />Saving…</p>}
          {invitation.kind !== 'none' && <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-900 dark:bg-emerald-950/30">
            <p role="status" className="flex items-center gap-2 text-sm font-semibold text-emerald-800 dark:text-emerald-200">{invitation.kind === 'copied' ? <Check className="size-4" /> : <Link2 className="size-4" />}{invitation.kind === 'copied' ? 'Link copied. Ready to share.' : 'Link ready. Copy it below.'}</p>
            <input aria-label="Guest sharing link" readOnly value={invitation.url} onFocus={(e) => e.currentTarget.select()} className="mt-2 w-full rounded-lg border border-emerald-200 bg-white p-2 text-xs text-stone-700 dark:border-emerald-800 dark:bg-stone-950 dark:text-stone-200" />
            <button type="button" onClick={() => void copy(invitation.shareId, invitation.url)} disabled={busy !== null} className="mt-2 inline-flex min-h-10 items-center gap-2 rounded-lg px-2 text-sm font-medium text-emerald-800 hover:bg-emerald-100 dark:text-emerald-200 dark:hover:bg-emerald-900"><Copy className="size-4" />Copy link</button>
          </div>}
          {error && <p role="alert" className="mt-3 rounded-lg bg-red-50 p-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-200">{error}</p>}
          <p className="mt-3 text-xs leading-5 text-stone-500 dark:text-stone-400">Anyone with the link can read this conversation in a browser. They need no account or app. Their replies wait for your approval.</p>
          {activeShares.length > 0 && <section className="mt-4 border-t border-stone-200 pt-3 dark:border-stone-700"><h3 className="text-sm font-semibold">Active links and guest requests</h3>
            {activeShares.map((share) => <div key={share.id} className="mt-2 rounded-xl border border-stone-200 p-3 dark:border-stone-700">
              <div className="flex items-start justify-between gap-2"><div><p className="text-xs font-medium">{share.name ? `Guest: ${share.name}` : 'Not joined yet'}</p><p className="mt-1 text-xs text-stone-500">{share.expires === null ? 'Until revoked' : `Expires ${new Date(share.expires * 1000).toLocaleString()}`}</p></div><button type="button" onClick={() => void act(share.id, null, 'revoke')} disabled={busy !== null} className="min-h-10 rounded-lg px-2 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50 dark:text-red-300 dark:hover:bg-red-950/40">Revoke</button></div>
              {share.proposals.filter((proposal) => ['pending', 'sending', 'uncertain'].includes(proposal.status)).map((proposal) => <div key={proposal.id} className="mt-3 border-t border-stone-100 pt-3 dark:border-stone-800"><p className="whitespace-pre-wrap break-words text-sm">{proposal.body}</p><p className="mt-1 text-xs text-stone-500">{proposal.status === 'pending' ? 'Waiting for your approval' : 'Delivery needs confirmation'}</p><div className="mt-2 flex flex-wrap gap-2">{proposal.status === 'pending' ? <><button type="button" onClick={() => void act(share.id, proposal.id, 'approve')} disabled={busy !== null} className="min-h-10 rounded-lg bg-blue-600 px-3 text-xs font-medium text-white disabled:opacity-50">Approve and send</button><button type="button" onClick={() => void act(share.id, proposal.id, 'dismiss')} disabled={busy !== null} className="min-h-10 rounded-lg px-3 text-xs hover:bg-stone-100 disabled:opacity-50 dark:hover:bg-stone-800">Dismiss</button></> : <button type="button" onClick={() => void act(share.id, proposal.id, 'reconcile')} disabled={busy !== null} className="min-h-10 rounded-lg px-3 text-xs hover:bg-stone-100 disabled:opacity-50 dark:hover:bg-stone-800">Check delivery</button>}</div></div>)}
            </div>)}
          </section>}
        </div>
      </div>, document.body,
    )}
  </>
}
